<template>
  <v-dialog v-model="showDialog" fullscreen scrollable transition="dialog-bottom-transition">
    <v-card>
      <v-card-title class="secondary white--text py-3 px-5">
        <span class="text-subtitle-1 font-weight-bold">Gate Activity & Container Type Breakdown</span>
        <v-spacer />
        <v-btn icon color="white" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
          <span class="caption grey--text mr-2">Based on the currently applied filters on the list.</span>
          <v-spacer />
          <span class="caption grey--text mr-1">From</span>
          <DateField
            :inputProps="{ dense: true, outlined: true, hideDetails: 'auto', style: 'width: 130px' }"
            v-model="dateRange.from"
            @change="onFromChange"
          />
          <span class="caption grey--text mx-1">To</span>
          <DateField
            :inputProps="{ dense: true, outlined: true, hideDetails: 'auto', style: 'width: 130px' }"
            v-model="dateRange.to"
            @change="onToChange"
          />
          <v-btn small text color="primary" @click="clearDateRange" v-if="dateRange.from || dateRange.to">
            Clear
          </v-btn>
        </div>

        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
          <div class="mb-6">
            <div class="overline grey--text mb-1">Gate Out / Gate In / DA Created / Trip Created / Completed / Failed — Day Wise</div>
            <div class="chart-wrap" ref="containerChartWrap">
              <canvas ref="containerChart"></canvas>
            </div>
            <div class="d-flex align-center mt-2" style="gap: 16px">
              <span class="caption d-flex align-center"><span class="legend-dot" style="background:#14b8a6"></span>Gate Out</span>
              <span class="caption d-flex align-center"><span class="legend-dot" style="background:#3b82f6"></span>Gate In</span>
              <span class="caption d-flex align-center"><span class="legend-dot" style="background:#f59e0b"></span>DA Created</span>
              <span class="caption d-flex align-center"><span class="legend-dot" style="background:#a855f7"></span>Trip Created</span>
              <span class="caption d-flex align-center"><span class="legend-dot" style="background:#16a34a"></span>Completed</span>
              <span class="caption d-flex align-center"><span class="legend-dot" style="background:#ef4444"></span>Failed</span>
            </div>
          </div>

          <div class="d-flex flex-wrap" style="gap: 48px">
            <div>
              <div class="overline grey--text mb-1">Container Type Breakdown</div>
              <div class="chart-wrap" ref="containerTypeChartWrap" style="max-width: 480px">
                <canvas ref="containerTypeChart"></canvas>
              </div>
            </div>

            <div>
              <div class="overline grey--text mb-1">Sub Hire Breakdown</div>
              <div class="chart-wrap" ref="subhireChartWrap" style="max-width: 480px">
                <canvas ref="subhireChart"></canvas>
              </div>
            </div>
          </div>

          <div v-if="!rows.length" class="pa-6 text-center caption grey--text">
            No gate activity for the current filters.
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Chart from "chart.js";
import DateField from "@/components/FormBaseComponents/DateField.vue";

export default {
  name: "EmergencyOrderContainerChartDialog",
  components: { DateField },
  props: {
    value: { type: Boolean, default: false },
    params: { type: Object, default: () => ({}) },
  },
  watch: {
    value(val) {
      if (val) {
        const today = new Date().toISOString().slice(0, 10);
        this.dateRange = { from: today, to: today };
        this.fetchSummary();
      }
    },
  },
  data() {
    return {
      loading: false,
      rows: [],
      containerTypes: [],
      subhireBreakdown: [],
      dateRange: { from: "", to: "" },
      _containerChart: null,
      _containerTypeChart: null,
      _subhireChart: null,
    };
  },
  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    formatDate(value) {
      return this.$globals.formatDate(value);
    },
    closeDialog() {
      this.showDialog = false;
    },
    clearDateRange() {
      this.dateRange = { from: "", to: "" };
      this.fetchSummary();
    },
    onFromChange() {
      if (this.dateRange.from && this.dateRange.to && this.dateRange.from > this.dateRange.to) {
        this.dateRange.to = this.dateRange.from;
      }
      this.fetchSummary();
    },
    onToChange() {
      if (this.dateRange.from && this.dateRange.to && this.dateRange.to < this.dateRange.from) {
        this.dateRange.from = this.dateRange.to;
      }
      this.fetchSummary();
    },
    fetchSummary() {
      this.loading = true;
      const requestParams = { ...this.params };
      if (this.dateRange.from) {
        requestParams.movement_date_from = this.dateRange.from;
      }
      if (this.dateRange.to) {
        requestParams.movement_date_to = this.dateRange.to;
      }
      this.$api.emergencyOrderContainer
        .getChartSummary(requestParams)
        .then((res) => {
          const data = res.data || res;
          this.rows = data.rows || [];
          this.containerTypes = data.container_types || [];
          this.subhireBreakdown = data.subhire_breakdown || [];
          this.loading = false;
          this.$nextTick(() => {
            this.renderContainerChart();
            this.renderContainerTypeChart();
            this.renderSubhireChart();
          });
        })
        .catch((err) => {
          console.error(err);
          this.loading = false;
          this.$bus.$emit("showToastMessage", {
            message: "Failed to load chart summary.",
            color: "error",
          });
        });
    },
    renderContainerChart() {
      if (this._containerChart) {
        this._containerChart.destroy();
        this._containerChart = null;
      }
      const wrap = this.$refs.containerChartWrap;
      const canvas = this.$refs.containerChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = 240;

      const labels = this.rows.map((row) => this.formatDate(row.date));
      this._containerChart = new Chart(canvas.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets: [
            { label: "Gate Out", backgroundColor: "#14b8a6", data: this.rows.map((row) => row.gate_out) },
            { label: "Gate In", backgroundColor: "#3b82f6", data: this.rows.map((row) => row.gate_in) },
            { label: "DA Created", backgroundColor: "#f59e0b", data: this.rows.map((row) => row.da_created) },
            { label: "Trip Created", backgroundColor: "#a855f7", data: this.rows.map((row) => row.trip_created) },
            { label: "Completed", backgroundColor: "#16a34a", data: this.rows.map((row) => row.completed) },
            { label: "Failed", backgroundColor: "#ef4444", data: this.rows.map((row) => row.failed) },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{
              gridLines: { display: false },
              ticks: { fontSize: 10, fontStyle: "bold", fontColor: "#475569", maxRotation: 0 },
            }],
            yAxes: [{ gridLines: { color: "#e2e8f0" }, ticks: { fontSize: 9, fontColor: "#94a3b8", beginAtZero: true } }],
          },
          tooltips: { mode: "index", intersect: false },
        },
      });
    },
    renderContainerTypeChart() {
      if (this._containerTypeChart) {
        this._containerTypeChart.destroy();
        this._containerTypeChart = null;
      }
      const wrap = this.$refs.containerTypeChartWrap;
      const canvas = this.$refs.containerTypeChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = 260;

      const palette = ["#14b8a6", "#3b82f6", "#f59e0b", "#a855f7", "#ef4444", "#16a34a", "#f97316"];
      const labels = this.containerTypes.map((row) => row.container_type);
      const data = this.containerTypes.map((row) => row.count);
      const colors = this.containerTypes.map((_, i) => palette[i % palette.length]);

      this._containerTypeChart = new Chart(canvas.getContext("2d"), {
        type: "doughnut",
        data: {
          labels,
          datasets: [{ data, backgroundColor: colors, borderWidth: 2, borderColor: "#fff" }],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: {
            position: "bottom",
            labels: {
              fontSize: 11,
              generateLabels: (chartInstance) => {
                const chartData = chartInstance.data;
                return chartData.labels.map((label, i) => ({
                  text: `${label}: ${chartData.datasets[0].data[i]}`,
                  fillStyle: chartData.datasets[0].backgroundColor[i],
                  index: i,
                }));
              },
            },
          },
          cutoutPercentage: 55,
          tooltips: {
            callbacks: {
              label: (item, chartData) => {
                const label = chartData.labels[item.index];
                const value = chartData.datasets[0].data[item.index];
                return `${label}: ${value}`;
              },
            },
          },
        },
      });
    },
    renderSubhireChart() {
      if (this._subhireChart) {
        this._subhireChart.destroy();
        this._subhireChart = null;
      }
      const wrap = this.$refs.subhireChartWrap;
      const canvas = this.$refs.subhireChart;
      if (!wrap || !canvas) return;
      canvas.width = wrap.clientWidth;
      canvas.height = 260;

      const colorMap = { Internal: "#3b82f6", "Sub Hire": "#f59e0b" };
      const labels = this.subhireBreakdown.map((row) => row.label);
      const data = this.subhireBreakdown.map((row) => row.count);
      const colors = this.subhireBreakdown.map((row) => colorMap[row.label] || "#94a3b8");

      this._subhireChart = new Chart(canvas.getContext("2d"), {
        type: "doughnut",
        data: {
          labels,
          datasets: [{ data, backgroundColor: colors, borderWidth: 2, borderColor: "#fff" }],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          legend: {
            position: "bottom",
            labels: {
              fontSize: 11,
              generateLabels: (chartInstance) => {
                const chartData = chartInstance.data;
                return chartData.labels.map((label, i) => ({
                  text: `${label}: ${chartData.datasets[0].data[i]}`,
                  fillStyle: chartData.datasets[0].backgroundColor[i],
                  index: i,
                }));
              },
            },
          },
          cutoutPercentage: 55,
          tooltips: {
            callbacks: {
              label: (item, chartData) => {
                const label = chartData.labels[item.index];
                const value = chartData.datasets[0].data[item.index];
                return `${label}: ${value}`;
              },
            },
          },
        },
      });
    },
  },
  beforeDestroy() {
    if (this._containerChart) this._containerChart.destroy();
    if (this._containerTypeChart) this._containerTypeChart.destroy();
    if (this._subhireChart) this._subhireChart.destroy();
  },
};
</script>

<style scoped>
.chart-wrap {
  position: relative;
  width: 100%;
}
.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}
</style>
